import flatten from 'lodash/flatten';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { VirtualPortal, VirtualPortalContent } from '../types';
import { VirtualPortalContext } from '../types/VirtualPortalContext';

/**
 * Create a new virtual portal
 *
 * @template P Properties that are sent by the sender
 */
const createConnector = <P extends object = object>(): VirtualPortal<P> => {
    const ctx: VirtualPortalContext<P> = createContext({
        props: {} as VirtualPortalContent & P,
        update: null,
    });

    return {
        Channel: ({children}) => {
            const [props, update] = useState({} as VirtualPortalContent & P);

            const contextValue = useMemo(
                () => ({ props, update }),
                [props]
            );

            return <ctx.Provider value={contextValue}>
                {children}
            </ctx.Provider>;
        },

        Send: (props) => {
            const {update} = useContext(ctx);

            const propEntries = flatten(
                Object.entries(props)
                    .sort(([a], [b]) => a > b ? 1 : a < b ? -1 : 0)
            );

            useEffect(() => {
                if (update) {
                    update(props);
                }
                return () => {
                    if (update) {
                        update(null);
                    }
                };
            }, [update, propEntries.length, ...propEntries]);

            return update ? null : <>{props?.children}</>;
        },

        Receive: (props) => {
            const {props: ctxContent} = useContext(ctx);

            const children = (ctxContent?.children || props?.children) ?? null;
            const content = { ...ctxContent, children };

            return <>{props.render ? props.render(content) : children}</>;
        },
    };
};

export default createConnector;
